import * as moment from 'moment';
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useRouter } from 'next/router';
import { Box, Typography, Link } from '@mui/material';
import PlanField from 'components/PlanField';
import CustomButton from 'components/CustomButton';
import AlertAlt from 'components/AlertAlt';
import { Card, Title, BillingInterval } from 'styles/components/planDetailsCard';
import { useStyles } from './style';
import { benefitsPricing } from 'utils/benefitsPricing';
import { openMtgChat } from 'utils';
import { Icons } from 'components/Icons';
import Image from 'next/image';
import PaymentService from 'services/PaymentService';
import CancelSubscriptionModalAlternative from 'components/CancelSubscriptionModalAlternative';

const Props = {
  user: PropTypes.object.isRequired,
  plan: PropTypes.object,
  changePlanAction: PropTypes.bool,
  paymentMethodUpdate: PropTypes.bool,
};

export default function OwnerPlanDetailsCardAlt({ user, plan }) {
  const router = useRouter();
  const classes = useStyles();
  const [itemsBenefits, setItemsBenefits] = useState(6);
  const [nextRenewal, setNextRenewal] = useState(null);
  const [daysToRenew, setDaysToRenew] = useState(null);
  const paymentService = new PaymentService();
  const [priceNewRenewal, setPriceNewRenewal] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [planBillingInterval, setPlanBillingInterval] = useState(null);

  async function getPaymentInfo() {
    const paymentInfo = await paymentService.getStripeSubscription();
    setPlanBillingInterval(paymentInfo.data?.plan?.interval);
    setPriceNewRenewal(paymentInfo.data?.items?.data?.[0]?.price?.unit_amount);
  }

  // function handleOpenIntercomChat() {
  //   if (typeof window.Intercom === 'function') {
  //     window.Intercom('show');
  //   }
  // }
  function handleOpenIntercomChat() {
    openMtgChat();
  }

  useEffect(() => {
    if (plan && plan.nextCharge) {
      const nextRenewalDate = new Date(plan.nextCharge);
      setNextRenewal(nextRenewalDate);
    } else {
      setNextRenewal(null);
    }
  }, [plan]);

  useEffect(() => {
    if (nextRenewal) {
      const currentDate = new Date();
      const daysForNextRenewal = Math.ceil((nextRenewal - currentDate) / (1000 * 60 * 60 * 24));
      setDaysToRenew(daysForNextRenewal);
    }
  }, [nextRenewal]);

  useEffect(() => {
    getPaymentInfo();
  }, [planBillingInterval, priceNewRenewal]);

  const planTitles = {
    R: 'Researcher Subscription',
    L: 'Teams & Labs Subscription',
    S: 'Student Subscription',
    T: 'Starter Subscription',
  };

  function benefitsList() {
    return (
      <>
        <Box display="flex" justifyContent="center" width="100%">
          <Box display="flex" alignItems="center" pt={4}>
            <div className={classes.line} />
            <Typography style={{ color: '#D6246E', fontSize: 18, padding: '6px' }}>
              <b>You have access to:</b>
            </Typography>
            <div className={classes.line} />
          </Box>
        </Box>
        <Box display="flex" alignItems="start">
          <Box className={classes.benefitsList}>
            {benefitsPricing.slice(0, itemsBenefits).map((benefit) => (
              <div key={benefit.id}>
                <Icons.done />
                <p>{benefit.text}</p>
              </div>
            ))}

            <Box pt={2}>
              {itemsBenefits === 6 ? (
                <Typography
                  className={classes.allFeatures}
                  component="p"
                  onClick={() => setItemsBenefits(17)}
                >
                  See All Features
                </Typography>
              ) : (
                <Typography
                  className={classes.allFeatures}
                  component="p"
                  onClick={() => setItemsBenefits(5)}
                >
                  See less features
                </Typography>
              )}
            </Box>
          </Box>

          <Box pt={1}>
            <Image src="/images/cool-scientist.svg" alt="cool scientist" width={316} height={193} />
          </Box>
        </Box>
      </>
    );
  }

  if (plan) {
    return (
      <Card>
        <Box>
          <Typography className={classes.title}>
            {planBillingInterval === 'year' ? 'Yearly' : 'Monthly'} Plan
          </Typography>

          {!plan.cancelAt && (
            <Box display="flex" alignItems="baseline">
              {plan.total > 0 ? (
                <Typography className={classes.priceText}>
                  {plan.currency} {plan.total}
                </Typography>
              ) : (
                <Box pr={0.5} display="flex" alignItems="baseline">
                  <Typography className={classes.priceText}>Free Trial</Typography>
                  <span style={{ color: '#D6246E', fontSize: 14, marginRight: 4 }}>
                    {daysToRenew} days
                  </span>
                  <BillingInterval>
                    left, then {plan.currency} {priceNewRenewal / 100}
                  </BillingInterval>
                </Box>
              )}

              <BillingInterval>per {planBillingInterval}</BillingInterval>
            </Box>
          )}

          {plan.cancelAt && (
            <Box pt={2}>
              <Typography>
                Your plan has been <b>canceled</b> and will be changed to <b>Starter</b> on{' '}
                {moment.utc(plan.cancelAt).format('MM/DD/YYYY')}
              </Typography>
            </Box>
          )}

          {benefitsList()}

          {!plan.cancelAt && (
            <Box pt={4}>
              <CustomButton
                onClick={() => setShowModal(true)}
                variant="ctaPinkOutline"
                style={{ width: '100%' }}
              >
                Cancel subscription
              </CustomButton>
            </Box>
          )}

          {plan.cancelAt && (
            <Box pt={4}>
              <CustomButton
                onClick={() => router.push('/pricing-alt/')}
                variant="ctaPinkOutline"
                style={{ width: '100%' }}
              >
                Change plan
              </CustomButton>
            </Box>
          )}

          <CancelSubscriptionModalAlternative
            plan={plan}
            open={showModal}
            onClose={() => setShowModal(false)}
          />
        </Box>
      </Card>
    );
  }

  return (
    <Card>
      <Title>{planTitles[user.tp_user]}</Title>

      <Box mt={3} mb={3}>
        <PlanField
          icon="schedule"
          label="Next Renewal"
          value={moment.utc(user.dt_end).format('MM/DD/YYYY')}
        />
      </Box>

      <Box mb={3}>
        <AlertAlt icon={false} severity="warning">
          Due to an update in the payment system, some older accounts may not be managed directly in
          the website. Please{' '}
          <Link onClick={handleOpenIntercomChat}>contact the customer success team</Link> for any
          request.
        </AlertAlt>
      </Box>
    </Card>
  );
}

OwnerPlanDetailsCardAlt.propTypes = Props;
